import { SelectProps, StatusIndicatorProps } from '@amzn/awsui-components-core';
import { BusinessGroupEntity } from './AppContextModels';
import { PlanningCycleEntity } from './PlanningCycleModel';
import { ForecastGridFixedFields } from 'src/features/business-group/forecast-template/forecast-utils/ForecastGridConstants';
import { ValidationStatusEntity } from './XptGenericModels';

/**
 * Raw forecast line item data structure as retrieved from S3 storage.
 * This represents a single forecast entry with all financial and metadata information
 * before any UI transformations are applied.
 *
 * @interface ForecastRowDataStructured
 */
export interface ForecastRowDataStructured {
  scenario_seq_id: number;
  forecast_input_id: number;
  xpt_line_item_id: string;
  xpt_line_item_seq_id: number;

  is_new: boolean;

  budget_owner: string;
  budget_type: string;

  local_currency: string;
  is_planned_in_usd: boolean;

  local_to_usd_fx_rate: number;
  usd_to_local_fx_rate: number;

  corp_segments: any[];
  bus_segments: any[];

  actual_months: MonthlyAmount[];
  forecast_months: MonthlyAmount[];

  updated_at: string;
  updated_by: string;

  /** List of PO numbers tagged to this line item (e.g., ["63-18611277", "E9-19845927"]) */
  po_list?: string[];
}

/**
 * Represents a single month's financial data as a date-to-amount mapping.
 * The key is a date string in YYYYMMDD format, value is the monetary amount.
 *
 * @interface MonthlyAmount
 * @description Used in both actual_months and forecast_months arrays
 * @example
 * ```typescript
 * const monthData: MonthlyAmount = { "20250801": 435 }; // August 2025: $435
 * const emptyMonth: MonthlyAmount = { "20250901": null }; // September 2025: no data
 * ```
 */
export interface MonthlyAmount {
  // [date: string]: number | null;
  [key: string]: any;
}

/**
 * Transformed forecast data optimized for AG Grid display and user interaction.
 * Contains both original S3 data and additional frontend metadata for grid operations.
 *
 * @interface ForecastGridRowData
 * @description Extends raw S3 data with UI state management fields
 * @see ForecastRowDataStructured - The source data structure this is derived from
 */
export interface ForecastGridRowData {
  // FE metadata fields
  [ForecastGridFixedFields.RowId.value]: string;
  [ForecastGridFixedFields.IsNewFERow.value]: boolean;
  [ForecastGridFixedFields.IsTouched.value]: boolean;
  [ForecastGridFixedFields.IsEdited.value]: boolean;
  [ForecastGridFixedFields.IsActive.value]: boolean;
  [ForecastGridFixedFields.IsSegmentEdited.value]: boolean;

  [ForecastGridFixedFields.ScenarioSeqId.value]: number | null;
  [ForecastGridFixedFields.ForecastInputId.value]: number | null;
  [ForecastGridFixedFields.XptLineItemId.value]: string | null;
  [ForecastGridFixedFields.XptLineItemSeqId.value]: number | null;

  [ForecastGridFixedFields.IsNew.value]: boolean;

  [ForecastGridFixedFields.BudgetOwner.value]: string;
  [ForecastGridFixedFields.BudgetType.value]: string | null;

  [ForecastGridFixedFields.LOCAL_CURRENCY.value]: string | null;
  [ForecastGridFixedFields.IS_PLANNED_IN_USD.value]: boolean;
  [ForecastGridFixedFields.LOCAL_TO_USD_FX_RATE.value]: number | null;
  [ForecastGridFixedFields.USD_TO_LOCAL_FX_RATE.value]: number | null;

  [ForecastGridFixedFields.UpdatedAt.value]: string;
  [ForecastGridFixedFields.UpdatedBy.value]: string;

  [key: string]: any;
}

export interface ForecastInputSubmissionModel {
  // FE metadata fields
  [ForecastGridFixedFields.RowId.value]: string;
  [ForecastGridFixedFields.IsNewFERow.value]: boolean;
  [ForecastGridFixedFields.IsTouched.value]: boolean;
  [ForecastGridFixedFields.IsEdited.value]: boolean;
  [ForecastGridFixedFields.IsActive.value]: boolean;
  [ForecastGridFixedFields.IsSegmentEdited.value]: boolean;

  [ForecastGridFixedFields.ScenarioSeqId.value]: number | null;
  [ForecastGridFixedFields.ForecastInputId.value]: number | null;
  [ForecastGridFixedFields.XptLineItemId.value]: string | null;
  [ForecastGridFixedFields.XptLineItemSeqId.value]: number | null;

  [ForecastGridFixedFields.IsNew.value]: boolean;

  [ForecastGridFixedFields.BudgetOwner.value]: string;
  [ForecastGridFixedFields.BudgetType.value]: string | null;

  [ForecastGridFixedFields.LOCAL_CURRENCY.value]: string | null;
  [ForecastGridFixedFields.IS_PLANNED_IN_USD.value]: boolean;
  [ForecastGridFixedFields.LOCAL_TO_USD_FX_RATE.value]: number | null;
  [ForecastGridFixedFields.USD_TO_LOCAL_FX_RATE.value]: number | null;

  corp_segments: any[];
  bus_segments: any[];

  forecast_months: MonthlyAmount[];

  [ForecastGridFixedFields.UpdatedAt.value]: string;
  [ForecastGridFixedFields.UpdatedBy.value]: string;
}

/**
 * Metadata about forecast input operations and S3 storage details.
 * Contains information about where and how forecast data is stored and processed.
 *
 * @interface ForecastInputMutation
 * @description Used for tracking data lineage and S3 file management
 */
export interface ForecastInputMutation {
  data_classification_id: number;
  scenario_seq_id: number;
  s3_bucket_name: string;
  s3_region: string;
  s3_update_file_key: string;
  updated_at: string;
  updated_by: string;
  operation_type: string;
}

/**
 * Filter selections for corporate segments, mapping segment keys to their selected option values.
 * Used for filtering forecast data based on corporate segment criteria.
 *
 * @interface CorpSegmentFilterSelection
 * @description Key-value mapping where keys are corp segment identifiers and values are selected options
 */
export interface CorpSegmentFilterSelection {
  [corpSegmentKey: string]: SelectProps.Options;
}

/**
 * Status types for dropdown loading states.
 * Represents the current state of data fetching for dropdown options.
 *
 * @typedef {('pending' | 'loading' | 'finished' | 'error')} DropdownStatusType
 */
export type DropdownStatusType = 'pending' | 'loading' | 'finished' | 'error';

/**
 * Configuration and state for corporate segment dropdown components.
 * Contains metadata and options for rendering dynamic corporate segment dropdowns.
 *
 * @interface ForecastTemplateCorpSegmentDropdowns
 * @description Used in forecast template forms for segment selection
 */
export interface ForecastTemplateCorpSegmentDropdowns {
  key: number;
  segmentKey: string;
  displayName: string;
  isRequired: boolean;
  isMultiSelect: boolean;
  fieldDropdownOptions: SelectProps.Options;
  loadingStatus: DropdownStatusType;
}

export interface FetchTemplateColumnDataParams {
  relevantBusinessGroupMetadata: BusinessGroupEntity;
  relevantPlanningCyclesActive: PlanningCycleEntity[];
  filterSelection: any;
  isLocked: boolean;
}

/**
 * State container for forecast template data with status information.
 * Tracks the current state of forecast data to provide user feedback.
 *
 * @interface ForecastTemplateDataState
 */
export interface ForecastTemplateDataState {
  dataStatus: DataStatus;
}

/**
 * Data freshness and modification states for forecast templates.
 * Indicates whether data needs to be refreshed or has unsaved changes.
 *
 * @typedef {('NotLoaded' | 'Latest' | 'Modified' | 'Outdated')} DataStatusType
 */
export type DataStatusType = 'NotLoaded' | 'Latest' | 'Modified' | 'Outdated';

/**
 * Complete data status information including display properties for UI indicators.
 * Provides both the status type and visual representation details.
 *
 * @interface DataStatus
 * @description Used to render status indicators in the UI
 */
export type DataStatus = {
  status: DataStatusType;
  statusDisplayName: string;
  statusType: StatusIndicatorProps.Type;
  statusColor: StatusIndicatorProps.Color;
  tooltip: string;
};

/**
 * Standard dropdown option structure for form components.
 * Provides consistent option format across the application.
 *
 * @interface OptionDropdown
 */
export interface OptionDropdown {
  label: string;
  value: string;
  description?: string;
}

export interface ForecastTemplateMasterCorpSegmentDropdownValues {
  masterCorpSegmentDisplayName: string;
  masterCorpSegmentDropdownValues: OptionDropdown[];
}

export interface CostCenterAndProductLineValuesPerBusinessGroup {
  dataClassificationId: number;
  data_classification_short_description: string;
  costCenters: string[];
  productLines: string[];
}

export interface ForecastTemplateColumns {
  metadataColumns: string[];
  forecastTemplateImportFileHeader: string[];
  mandatoryFields: string[];
  corpAndBussSegmentsMandatoryFields: string[];
  modifiedCheckFields: string[];
  corpSegmentOptionalFields: string[];
  corpSegmentMandatoryFields: string[];
  corpSegmentColumns: string[];
  businessSegmentColumns: string[];
  businessSegmentMandatoryFields: string[];
  businessSegmentDropdownFields: string[];
  actualMonthColumnIds: string[];
  actualMonthsDisplayFormat: string[];
  forecastMonthColumnsIds: string[];
  forecastMonthsDisplayFormat: string[];
  addNewRowColumns: string[];
}

export interface TransformedRowItem {
  corp_segments: string[];
  bus_segments: string[];
  forecast_months: { [key: string]: any }[];
  [key: string]: any;
}

/**
 * Default validation status when validation has not yet been initiated.
 * Provides consistent initial state for validation processes.
 *
 * @constant {ValidationStatusEntity} VALIDATION_NOT_INITIATED
 */
export const VALIDATION_NOT_INITIATED: ValidationStatusEntity = {
  colorOverride: 'grey',
  validationStatus: 'pending',
  validationMessage: 'Not initiated',
  validationDefaultMessage: '',
  validationErrorDetails: []
};

/**
 * Validation status indicating that validation is currently in progress.
 * Used to show loading state during validation operations.
 *
 * @constant {ValidationStatusEntity} VALIDATION_STARTED
 */
export const VALIDATION_STARTED: ValidationStatusEntity = {
  colorOverride: 'grey',
  validationStatus: 'loading',
  validationMessage: 'Validating',
  validationDefaultMessage: '',
  validationErrorDetails: []
};

/**
 * Comprehensive validation status tracking for forecast template data.
 * Contains validation results for all critical data validation categories.
 *
 * @interface ForecastTemplateDataValidationStatus
 * @description Each property represents a specific validation rule with its current status
 * @example
 * ```typescript
 * const validationStatus: ForecastTemplateDataValidationStatus = {
 *   HeadersMatching: VALIDATION_NOT_INITIATED,
 *   CostCenterValidation: VALIDATION_STARTED,
 *   // ... other validation statuses
 * };
 * ```
 */
export interface ForecastTemplateDataValidationStatus extends Record<string, any> {
  HeadersMatching: ValidationStatusEntity;
  CostCenterValidation: ValidationStatusEntity;
  BudgetOwnerValidation: ValidationStatusEntity;
  ForecastMonthValidation: ValidationStatusEntity;
  MandatoryFieldValidation: ValidationStatusEntity;
  NonEditableFieldValidations: ValidationStatusEntity;
  UnAuthorizedRows: ValidationStatusEntity;
  SegmentsValidation: ValidationStatusEntity;
  DuplicateRecordValidation: ValidationStatusEntity;
  RepeatedRecordValidation: ValidationStatusEntity;
  BudgetTypeAccountMappingValidation: ValidationStatusEntity;
  CurrencyFieldValidation: ValidationStatusEntity;
  BudgetOwnerCostCenterValidation: ValidationStatusEntity;
  DMMBusinessSegmentValidation: ValidationStatusEntity;
  BudgetEditableMonthsValidation: ValidationStatusEntity;
}
